/**
 * The few helpers the design's markup needs once it is copied into JSX (see
 * scratchpad batchG/tpl2jsx.mjs, which emits calls to these):
 *
 *   sx(css)   the design's inline style string as a React style (shared, memoised)
 *   hov(css)  the design's style-hover: a class whose :hover applies `css` over the inline style
 *   ck(fn)    an `<a href="#">` click: never navigates, then calls the design's handler
 *   pd        an `<a href="#">` with no handler
 */
import type { MouseEvent } from 'react';
import { sx } from '../../ui/designStyles';

export { sx };

const hovers = new Map<string, string>();
let sheet: CSSStyleSheet | null = null;

/** A class name for the design's style-hover="..." (inline styles win unless !important). */
export function hov(css: string): string {
  let cls = hovers.get(css);
  if (cls) return cls;
  cls = 'slh' + hovers.size.toString(36);
  hovers.set(css, cls);
  if (typeof document !== 'undefined') {
    if (!sheet) {
      const el = document.createElement('style');
      el.setAttribute('data-sales-hover', '');
      document.head.appendChild(el);
      sheet = el.sheet;
    }
    const decl = css
      .split(';')
      .map((d) => d.trim())
      .filter(Boolean)
      .map((d) => d + ' !important')
      .join(';');
    try {
      sheet?.insertRule(`.${cls}:hover{${decl}}`, sheet.cssRules.length);
    } catch {
      /* a rule the browser cannot parse is skipped */
    }
  }
  return cls;
}

type Handler = ((e: MouseEvent<HTMLElement>) => void) | undefined | null;

/** An anchor click that never follows href="#", then runs the handler. */
export const ck = (fn: Handler) => (e: MouseEvent<HTMLElement>) => {
  e.preventDefault();
  if (fn) fn(e);
};

export const pd = (e: { preventDefault?: () => void }) => {
  if (e && e.preventDefault) e.preventDefault();
};
